import PageContent from "ui/PageContent";
import Panel from "ui/Panel";
import React, { useState } from "react";
import { Link, Navigate, useLocation, useParams } from "react-router-dom";

import Loading from "ui/Loading";
import { formatMs } from "utils/format";
import PageSecondaryNavigation, {
  PageNavLink,
} from "ui/PageSecondaryNavigation";
import { useRoutes } from "utils/routes";
import Grid, { NumberCell } from "ui/Grid";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBolt,
  faCircleX,
  faExclamationTriangle,
  faTriangleExclamation,
} from "@fortawesome/pro-solid-svg-icons";
import { WorkbookSidebar } from "../WorkbookVariantSidebar";
import Select from "ui/Select";
import Popover from "ui/Popover";

import {
  WorkbookDetailsQuery,
  useWorkbookDetailsQuery,
} from "./gql/Query.generated";
import {
  ExplainComparisonWorkbookQuery,
  useExplainComparisonWorkbookQuery,
} from "./gql/Query.comparison.generated";
export type WorkbookType = WorkbookDetailsQuery["getWorkbookDetails"];
export type WorkbookVariantType =
  WorkbookDetailsQuery["getWorkbookDetails"]["variants"][number];
export type AliasParamMapType =
  WorkbookDetailsQuery["getWorkbookDetails"]["aliasParamMapList"][number];
type ExplainComparisonWorkbookDetailsType =
  ExplainComparisonWorkbookQuery["getWorkbookDetails"];

import ChooseParameters from "../ChooseParameters";
import ExplainCompareSidebar from "features/Explain/ExplainCompareSidebar";
import WithExplainPlan, {
  ComparablePlanType,
} from "features/Explain/WithExplainPlan";
import WithNodeSelection from "features/Explain/WithExplainNodeSelection";
import {
  ExplainComparison,
  useComparisonPlans,
} from "features/Explain/ExplainComparison";
import { mru } from "utils/array";
import ExplainCompareLinkBar from "../ExplainCompareLinkBar";
import ExplainFingerprint from "features/Explain/ExplainFingerprint";
import ParameterSetsPanel from "../ParameterSetsPanel";
import ActivityPanel from "../ActivityPanel";

type WorkbookTabType = "compare" | "params" | "activity";

const Workbook = ({ tab }: { tab?: WorkbookTabType }) => {
  const { workbookId, databaseId } = useParams();
  const { databaseWorkbookVariantResult } = useRoutes();

  const {
    loading: detailsLoading,
    error: detailsError,
    data: detailsData,
  } = useWorkbookDetailsQuery({
    variables: { workbookId, databaseId },
  });
  const {
    loading: comparisonLoading,
    error: comparisonError,
    data: comparisonData,
  } = useExplainComparisonWorkbookQuery({
    variables: {
      workbookId,
    },
    skip: tab != "compare",
  });

  const loading = detailsLoading || comparisonLoading;
  const error = detailsError ?? comparisonError;

  if (loading || error) {
    return <Loading error={!!error} />;
  }

  const workbook = detailsData.getWorkbookDetails;
  // During the initial workbook creation flow:
  // If the selection is not done yet, show the selection page
  if (!workbook.parameterSetsSelected) {
    return <ChooseParameters workbook={workbook} />;
  }

  const { blockSize } = workbook.server;

  const comparablePlans: ComparablePlanType[] = [];
  if (tab === "compare") {
    [comparisonData.getWorkbookDetails.baselineQuery]
      .concat(comparisonData.getWorkbookDetails.variants)
      .forEach((variant) =>
        variant.workbookResults.forEach((e) => {
          const label = `${variant.name} - ${getParameterSetName(
            e.parameterSetId,
            comparisonData.getWorkbookDetails,
          )}`;
          // Do not add results without fingerprint to the comparablePlans
          // as these can be currently-running, errored, or skipped ones (running EXPLAINs using collector)
          e.planFingerprint &&
            comparablePlans.push({
              id: e.id,
              label,
              url: databaseWorkbookVariantResult(
                workbook.databaseId,
                workbook.id,
                variant.id,
                e.id,
              ),
              seenAt: e.createdAt,
              fingerprint: e.planFingerprint,
              runtime: e.runtimeMs,
              ioMs: e.totalBlkReadTime,
              ioBytes: e.totalSharedBlksRead * blockSize,
              totCost: e.totalCost,
              plan: e.annotatedJson,
            });
        }),
      );
  }

  return (
    <WorkbookContent
      workbook={workbook}
      blockSize={blockSize}
      comparablePlans={comparablePlans}
      tab={tab}
      permittedToCreateWorkbooks={
        detailsData.getWorkbookDetails.database.permittedToCreateWorkbooks
      }
    />
  );
};

const WorkbookContent = ({
  workbook,
  blockSize,
  comparablePlans,
  tab,
  permittedToCreateWorkbooks,
}: {
  workbook: WorkbookType;
  blockSize: number;
  comparablePlans: ComparablePlanType[];
  tab: WorkbookTabType;
  permittedToCreateWorkbooks: boolean;
}) => {
  const { databaseWorkbookCompare } = useRoutes();
  const { databaseId, workbookId } = useParams();
  const [plan, comparePlan] = useComparisonPlans(comparablePlans);

  const layout = tab === "compare" || !tab ? "sidebar" : "default";

  function linkToComparison(planA: string, planB?: string) {
    return databaseWorkbookCompare(databaseId, workbookId, planA, planB);
  }

  return (
    <WithExplainPlan plan={plan} comparePlan={comparePlan}>
      <WithNodeSelection>
        <PageContent
          windowTitle={`EXPLAIN Workbook: ${workbook.name}`}
          title={workbook.name}
          pageCategory="workbooks"
          pageName="show"
          layout={layout}
          featureNav={
            <WorkbookFeatureNav
              workbookId={workbookId}
              databaseId={databaseId}
            />
          }
        >
          {tab == null && (
            <WorkbookOverviewPanel
              key="overview"
              databaseId={databaseId}
              workbook={workbook}
            />
          )}
          {tab == null && (
            <div key="overview-sidebar" className="2xl:w-[400px] w-[320px]">
              <WorkbookSidebar
                workbook={workbook}
                permittedToCreateWorkbooks={permittedToCreateWorkbooks}
              />
            </div>
          )}
          {tab === "compare" && (
            <ExplainComparisonLegacyRouteRedirect
              databaseId={databaseId}
              workbookId={workbookId}
            >
              <ExplainComparison
                key="plan-comparison"
                comparablePlans={comparablePlans}
                linkToComparison={linkToComparison}
                blockSize={blockSize}
              />
            </ExplainComparisonLegacyRouteRedirect>
          )}
          {tab === "compare" && (
            <div
              key="plan-comparison-sidebar"
              className="2xl:w-[480px] w-[400px]"
            >
              <ExplainCompareSidebar
                databaseId={databaseId}
                blockSize={blockSize}
              />
            </div>
          )}
          {tab === "params" && (
            <ParameterSetsPanel databaseId={databaseId} workbook={workbook} />
          )}
          {tab === "activity" && <ActivityPanel workbookId={workbook.id} />}
        </PageContent>
      </WithNodeSelection>
    </WithExplainPlan>
  );
};

const ExplainComparisonLegacyRouteRedirect = ({
  children,
  workbookId,
  databaseId,
}: {
  children: React.ReactNode;
  workbookId: string;
  databaseId: string;
}) => {
  const { databaseWorkbookCompare } = useRoutes();
  const { search } = useLocation();

  const params = new URLSearchParams(search);
  const planCompare = params.get("planCompare");
  if (!planCompare) {
    return children;
  }

  const [planA, _vs, planB] = planCompare.split("-");

  if (!planA) {
    return children;
  }

  return (
    <Navigate
      to={databaseWorkbookCompare(databaseId, workbookId, planA, planB)}
    />
  );
};

function getParameterSetName(
  parameterSetId: string,
  workbookDetails: ExplainComparisonWorkbookDetailsType,
): string {
  return (
    workbookDetails.parameterSets.find((set) => set.id === parameterSetId)
      ?.name ?? parameterSetId
  );
}

const WorkbookOverviewPanel = ({
  databaseId,
  workbook,
}: {
  databaseId: string;
  workbook: WorkbookType;
}) => {
  const {
    databaseWorkbookVariant,
    databaseWorkbookVariantResult,
    databaseWorkbookVariantRun,
  } = useRoutes();
  const [compareCandidates, setCompareCandidates] = useState<string[]>([]);
  function handleToggleCompareCandidate(
    e: React.ChangeEvent<HTMLInputElement>,
  ) {
    const { checked } = e.currentTarget;
    const candidateId = e.currentTarget.dataset.compareId;
    const newCandidates = checked
      ? mru(candidateId, compareCandidates, 2)
      : compareCandidates.filter((id) => id != candidateId);

    setCompareCandidates(newCandidates);
  }
  const [selectedParameterSet, setSelectedParameterSet] =
    useState<AliasParamMapType>(null);
  const handleParameterSetSelected = (selected: AliasParamMapType | null) => {
    setSelectedParameterSet(selected);
  };

  const secondaryTitle = (
    <Select
      placeholder="Filter by Parameter Set..."
      items={workbook.aliasParamMapList}
      itemToString={(item) => item.name}
      value={selectedParameterSet}
      onChange={handleParameterSetSelected}
    />
  );

  let fastestRuntime = Infinity;
  const data: Record<string, any>[] = [];

  [workbook.baselineQuery as WorkbookVariantType, ...workbook.variants].forEach(
    (exp) => {
      workbook.parameterSets.forEach((ps) => {
        const result = exp.workbookResults.find(
          (r) => r.parameterSetId === ps.id,
        );
        data.push({
          variantId: exp.id,
          variantName: exp.name,
          resultId: result?.id,
          planFingerprint: result?.planFingerprint,
          parameterSetName: ps.name,
          parameterSetId: ps.id,
          totalCost: result?.totalCost,
          runtimeMs: result?.runtimeMs,
          totalBlkReadTime: result?.totalBlkReadTime,
          errorMessage: result?.errorMessage,
          resultSource: result?.resultSource,
          resultStatus: result?.status,
        });
      });
    },
  );
  const filteredData = data
    .filter((dat) => {
      const filtered = selectedParameterSet
        ? dat.parameterSetId === selectedParameterSet.id
        : true;
      if (filtered) {
        fastestRuntime = Math.min(fastestRuntime, dat.runtimeMs ?? Infinity);
      }
      return filtered;
    })
    .sort((a, b) => {
      // sort data by variant ID first, set ID second
      const variantComparison = a.variantId - b.variantId;
      if (variantComparison !== 0) {
        return variantComparison;
      }
      // if variant IDs are equal, compare by set ID
      return a.parameterSetId - b.parameterSetId;
    });

  return (
    <>
      <div className="rounded-md bg-[#f7fafc] border border-[#E8E8EE] p-4 mb-4 grid gap-2 text-[#606060]">
        <div className="text-[18px] leading-6">All Query Plans</div>
        <div>
          Choose a query variant to see its query text and settings, or create a
          new variant. Variants can be used to test Postgres planner behavior or
          rewrite queries to improve performance.{" "}
          <a
            href="https://pganalyze.com/docs/query-tuning/query-optimization-examples"
            target="_blank"
          >
            Learn more.
          </a>
        </div>
      </div>
      <Panel title="Query Plans" secondaryTitle={secondaryTitle}>
        <Grid
          data={filteredData}
          columns={[
            {
              header: "",
              disableSort: true,
              field: "resultId",
              width: "34px",
              renderer: function CompareSelectCell({ fieldData, rowData }) {
                const checked = compareCandidates.includes(fieldData);
                return (
                  <input
                    type="checkbox"
                    data-compare-id={fieldData}
                    className="cursor-pointer !mt-0.5"
                    checked={checked}
                    onChange={handleToggleCompareCandidate}
                    disabled={!rowData.planFingerprint}
                  />
                );
              },
            },
            {
              field: "planFingerprint",
              header: "Plan",
              width: "16%",
              renderer: function PlanCell({ rowData, fieldData }) {
                if (rowData.resultId == null || fieldData == null) {
                  return (
                    <Link
                      to={databaseWorkbookVariantRun(
                        databaseId,
                        workbook.id,
                        rowData.variantId,
                      )}
                    >
                      Run EXPLAIN
                    </Link>
                  );
                }
                return (
                  <Link
                    to={databaseWorkbookVariantResult(
                      databaseId,
                      workbook.id,
                      rowData.variantId,
                      rowData.resultId,
                    )}
                  >
                    <ExplainFingerprint fingerprint={fieldData} />
                  </Link>
                );
              },
            },
            {
              field: "variantName",
              header: "Variant",
              width: "1fr",
              renderer: function VariantCell({ rowData, fieldData }) {
                return (
                  <Link
                    to={databaseWorkbookVariant(
                      databaseId,
                      workbook.id,
                      rowData.variantId,
                    )}
                  >
                    {fieldData}
                  </Link>
                );
              },
            },
            {
              field: "parameterSetName",
              header: "Parameter Set",
              width: "18%",
            },
            {
              field: "totalCost",
              header: "Est. Cost",
              width: "18%",
              renderer: NumberCell,
              style: "number",
              nullValue: "-",
            },
            {
              field: "runtimeMs",
              header: "Runtime",
              width: "18%",
              renderer: ({ fieldData, rowData }) => {
                return (
                  <RuntimeMsCell
                    fieldData={fieldData}
                    rowData={rowData}
                    fastestRuntime={fastestRuntime}
                  />
                );
              },
              style: "number",
            },
          ]}
        />
      </Panel>
      <ExplainCompareLinkBar
        databaseId={databaseId}
        workbookId={workbook.id}
        compareCandidates={compareCandidates}
      />
    </>
  );
};

export const IOReadPopover = ({
  totalBlkReadTime,
}: {
  totalBlkReadTime: number;
}) => {
  return (
    <Popover
      title={
        <>
          I/O Read Time:{" "}
          <span className="font-mono">{formatMs(totalBlkReadTime)}</span>
        </>
      }
      content={
        <>
          This query experienced I/O read time, indicating that the cache might
          not have been warmed up during execution. As a best practice, it's
          recommended to warm up the cache by running the query a few times
          before using EXPLAIN.
        </>
      }
      popupClassName="font-sans"
    >
      <FontAwesomeIcon icon={faExclamationTriangle} className="mr-[2px]" />
    </Popover>
  );
};

export const RuntimeMsCell = ({
  fieldData,
  rowData,
  fastestRuntime,
}: {
  fieldData: number;
  rowData: Record<string, any>;
  fastestRuntime: number;
}) => {
  const fastest = fieldData === fastestRuntime;
  if (fieldData == null) {
    // no runtime data - error, without analyze or parameter set without result
    // (with overview, parameter set without result is not listed, but listed in the variant page)

    if (rowData.errorMessage) {
      return (
        <Popover content={rowData.errorMessage}>
          <div className="text-[#D1242F]">
            <FontAwesomeIcon icon={faCircleX} className="mr-[2px]" />
            Error
          </div>
        </Popover>
      );
    }
    if (rowData.totalCost == null) {
      // parameter set without result
      return "-";
    }
    if (rowData.resultSource === "collector_upload") {
      if (rowData.resultStatus === "synced") {
        // collector + without analyze
        return (
          <Popover content="EXPLAIN ran without ANALYZE due to timeout (one minute).">
            <div>
              <FontAwesomeIcon
                icon={faTriangleExclamation}
                className="text-[#CA8A04] mr-[2px]"
              />
              Timeout
            </div>
          </Popover>
        );
      } else {
        // collector is still running EXPLAIN
        return "-";
      }
    }
    // manual + without analyze
    return (
      <Popover content="EXPLAIN uploaded without ANALYZE.">
        <div>
          <FontAwesomeIcon
            icon={faTriangleExclamation}
            className="text-[#CA8A04] mr-[2px]"
          />
          -
        </div>
      </Popover>
    );
  }
  return (
    <span className={fastest ? "text-[#2B5827]" : ""}>
      {rowData.totalBlkReadTime > 0 && (
        <IOReadPopover totalBlkReadTime={rowData.totalBlkReadTime} />
      )}
      {fastest && (
        <span title="Fastest" className="mr-[2px]">
          <FontAwesomeIcon icon={faBolt} />
        </span>
      )}
      {formatMs(fieldData)}
    </span>
  );
};

export const WorkbookFeatureNav = ({
  workbookId,
  databaseId,
}: {
  workbookId: string;
  databaseId: string;
}) => {
  const {
    databaseWorkbookVariants,
    databaseWorkbookCompare,
    databaseWorkbookParams,
    databaseWorkbookActivity,
  } = useRoutes();

  return (
    <PageSecondaryNavigation>
      <PageNavLink
        to={databaseWorkbookVariants(databaseId, workbookId)}
        end={false}
      >
        Overview
      </PageNavLink>
      <PageNavLink to={databaseWorkbookCompare(databaseId, workbookId)}>
        Compare Plans
      </PageNavLink>
      <PageNavLink to={databaseWorkbookParams(databaseId, workbookId)}>
        Parameter Sets
      </PageNavLink>
      <PageNavLink to={databaseWorkbookActivity(databaseId, workbookId)}>
        Activity
      </PageNavLink>
    </PageSecondaryNavigation>
  );
};

export default Workbook;
